import useRequest from "@/lib/request/useRequest";
import { Button, Card, CardBody, Input, Spinner } from "@heroui/react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState, useRef } from "react";
import useAuth from "@/lib/auth/useAuth";
import { MAPBOX_PUBLIC_TOKEN } from "@/lib/utils/map";
import { Organization } from "@/types/organization";
import useConfirm from "@/lib/confirm/useConfirm";

export const Route = createFileRoute("/(auth)/register/select-csp")({
    component: RouteComponent,
});

type CspPreference = "preferred" | "default";

type NearbyCsp = {
    id: number;
    name: string;
    logo_url: string | null;
    city: string | null;
    state: string | null;
    distance_miles: number | null;
    csp_preference?: CspPreference | null;
};

function RouteComponent() {
    const navigate = useNavigate();
    const { useGetRequest, useMutateRequest } = useRequest();

    const confirm = useConfirm();

    const auth = useAuth();
    const didAutofill = useRef(false);

    const farmOrgId = (auth.user?.organization as Organization)?.id;

    const setServiceProviderMutation = useMutateRequest({
        method: "put",
        errorMessage: "Could not save your CSP selection. Please try again.",
    });

    const [address, setAddress] = useState("");

    const [coords, setCoords] = useState<{ lat: number; lon: number } | null>(
        null,
    );

    const [isGeocoding, setIsGeocoding] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [resolvedPlaceLabel, setResolvedPlaceLabel] = useState<string | null>(
        null,
    );

    useEffect(() => {
        if (address.trim()) return;

        if (didAutofill.current) return;

        const u = auth.user;
        if (!u) return;

        const composed = [u.address_1, u.address_2, u.city, u.state, u.zip]
            .filter(Boolean)
            .map((s) => String(s).trim())
            .filter(Boolean)
            .join(", ");

        if (!composed) return;

        setAddress(composed);
        didAutofill.current = true;
    }, [auth.user, address]);

    const nearbyCspQuery = useGetRequest<NearbyCsp[]>({
        url: "organizations/nearby_csps/",
        enabled: !!coords,
        params: coords ? { lat: coords.lat, lon: coords.lon } : undefined,
        queryOptions: {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchOnReconnect: false,
        },
    });

    const { preferredCsps, defaultCsps } = useMemo(() => {
        const rows = nearbyCspQuery.data ?? [];

        const dist = (c: NearbyCsp) =>
            typeof c.distance_miles === "number"
                ? c.distance_miles
                : Number.POSITIVE_INFINITY;

        const preferred = rows
            .filter((c) => c.csp_preference === "preferred")
            .slice()
            .sort((a, b) => dist(a) - dist(b));

        const defaults = rows
            .filter((c) => c.csp_preference !== "preferred")
            .slice()
            .sort((a, b) => dist(a) - dist(b));

        return { preferredCsps: preferred, defaultCsps: defaults };
    }, [nearbyCspQuery.data]);

    const hasResults =
        !!coords && preferredCsps.length + defaultCsps.length > 0;

    const helperText = useMemo(() => {
        if (!coords) return null;

        if (resolvedPlaceLabel) {
            return `Using location: ${resolvedPlaceLabel}`;
        }

        return `Using coordinates: ${coords.lat.toFixed(5)}, ${coords.lon.toFixed(5)}`;
    }, [coords, resolvedPlaceLabel]);

    function confirmSelectCsp(csp: NearbyCsp) {
        const locationText =
            [csp.city, csp.state].filter(Boolean).join(", ") || "—";

        const distanceText =
            typeof csp.distance_miles === "number"
                ? `${csp.distance_miles.toFixed(1)} mi`
                : null;

        confirm.showConfirm({
            title: "Select this CSP?",
            description: `${csp.name} • ${locationText}${
                distanceText ? ` • ${distanceText}` : ""
            }`,
            confirmText: "Select",
            color: "primary",
            onConfirm: () => handleSelect(csp),
        });
    }

    async function handleSearch() {
        setError(null);

        const trimmed = address.trim();
        if (!trimmed) {
            setError("Enter an address to search.");
            return;
        }

        setIsGeocoding(true);
        setCoords(null);
        setResolvedPlaceLabel(null);

        try {
            const url = new URL(
                "https://api.mapbox.com/search/geocode/v6/forward",
            );

            url.searchParams.set("q", trimmed);
            url.searchParams.set("limit", "1");
            url.searchParams.set("autocomplete", "false");
            url.searchParams.set("types", "address,place,postcode");
            url.searchParams.set("access_token", MAPBOX_PUBLIC_TOKEN);

            const res = await fetch(url.toString(), { cache: "no-store" });
            if (!res.ok) {
                setError("Could not find that address. Please try again.");
                return;
            }
            const json = await res.json();
            const feature = json?.features?.[0];

            const lon = feature?.geometry?.coordinates?.[0];

            const lat = feature?.geometry?.coordinates?.[1];

            if (typeof lat !== "number" || typeof lon !== "number") {
                setError(
                    "Could not find a location for that address. Try a more specific address.",
                );
                return;
            }

            const label = String(
                feature?.properties?.full_address ?? "",
            ).trim();
            setResolvedPlaceLabel(label || null);

            setCoords({ lat, lon });
        } catch {
            setError("Could not find that address. Please try again.");
        } finally {
            setIsGeocoding(false);
        }
    }

    async function handleSelect(csp: NearbyCsp) {
        setError(null);

        if (!farmOrgId) {
            setError("Could not save your CSP selection. Please try again.");
            return;
        }

        try {
            await setServiceProviderMutation.mutateAsync({
                url: `organizations/${farmOrgId}/service_provider/`,
                data: { service_provider: csp.id },
            });

            await auth.refreshUser?.();

            navigate({ to: "/" });
        } catch {
            setError("Could not save your CSP selection. Please try again.");
        }
    }

    const isNearbyLoading =
        !!coords && (nearbyCspQuery.isLoading || nearbyCspQuery.isFetching);
    const nearbyErrored = !!coords && nearbyCspQuery.isError;
    const showNoResults =
        !!coords && !isNearbyLoading && !nearbyErrored && !hasResults;

    return (
        <div className="flex flex-col gap-3 p-4 text-sm max-w-xl mx-auto">
            <div className="flex flex-col gap-1">
                <div className="text-lg font-semibold">Find a CSP near you</div>
                <div className="text-foreground-500">
                    Enter your address and we’ll show nearby service providers.
                </div>
            </div>

            <div className="flex gap-2 items-end">
                <Input
                    label="Address"
                    value={address}
                    onValueChange={setAddress}
                    placeholder="Street Address, City, State, ZIP"
                />
                <Button
                    color="primary"
                    onPress={handleSearch}
                    isLoading={isGeocoding}
                >
                    Search
                </Button>
            </div>

            {helperText ? (
                <div className="text-xs text-foreground-500">{helperText}</div>
            ) : null}

            {error ? <div className="text-danger text-sm">{error}</div> : null}

            {isNearbyLoading ? (
                <div className="flex items-center gap-2 text-foreground-500">
                    <Spinner size="sm" />
                    Searching nearby CSPs…
                </div>
            ) : null}

            {nearbyErrored ? (
                <div className="text-danger text-sm">
                    Failed to load nearby CSPs. Please try again.
                </div>
            ) : null}

            {showNoResults ? (
                <div className="text-foreground-500">
                    No CSPs found within range.
                </div>
            ) : null}

            {hasResults ? (
                <div className="flex flex-col gap-4">
                    {preferredCsps.length ? (
                        <div className="flex flex-col gap-2">
                            <div className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
                                Preferred Providers
                            </div>

                            <div className="flex flex-col gap-2">
                                {preferredCsps.map((csp) => {
                                    const locationText =
                                        [csp.city, csp.state]
                                            .filter(Boolean)
                                            .join(", ") || "—";

                                    const distanceText =
                                        typeof csp.distance_miles === "number"
                                            ? `${csp.distance_miles.toFixed(1)} mi`
                                            : null;

                                    return (
                                        <Card
                                            key={csp.id}
                                            className="border-2 border-brandNavy-500/30 bg-brandNavy-500/5"
                                        >
                                            <CardBody className="flex flex-row items-center justify-between gap-3">
                                                <div className="flex items-center gap-3">
                                                    {csp.logo_url ? (
                                                        <img
                                                            src={csp.logo_url}
                                                            alt={`${csp.name} logo`}
                                                            className="h-10 w-10 object-contain"
                                                        />
                                                    ) : (
                                                        <div className="h-10 w-10 rounded bg-content2" />
                                                    )}

                                                    <div className="flex flex-col">
                                                        <div className="flex items-center gap-2">
                                                            <div className="font-medium">
                                                                {csp.name}
                                                            </div>

                                                            <span className="inline-flex items-center rounded-full bg-brandNavy-500/10 text-brandNavy-500 text-[11px] font-semibold px-2 py-0.5">
                                                                ★ Preferred
                                                            </span>
                                                        </div>

                                                        <div className="text-xs text-foreground-500">
                                                            {locationText}
                                                            {distanceText
                                                                ? ` • ${distanceText}`
                                                                : ""}
                                                        </div>
                                                    </div>
                                                </div>

                                                <Button
                                                    color="secondary"
                                                    onPress={() =>
                                                        confirmSelectCsp(csp)
                                                    }
                                                    isLoading={
                                                        setServiceProviderMutation.isPending
                                                    }
                                                    isDisabled={
                                                        setServiceProviderMutation.isPending
                                                    }
                                                >
                                                    Select
                                                </Button>
                                            </CardBody>
                                        </Card>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}

                    {defaultCsps.length ? (
                        <div className="flex flex-col gap-2">
                            <div className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
                                Providers
                            </div>

                            <div className="flex flex-col gap-2">
                                {defaultCsps.map((csp) => {
                                    const locationText =
                                        [csp.city, csp.state]
                                            .filter(Boolean)
                                            .join(", ") || "—";

                                    const distanceText =
                                        typeof csp.distance_miles === "number"
                                            ? `${csp.distance_miles.toFixed(1)} mi`
                                            : null;

                                    return (
                                        <Card key={csp.id}>
                                            <CardBody className="flex flex-row items-center justify-between gap-3">
                                                <div className="flex items-center gap-3">
                                                    {csp.logo_url ? (
                                                        <img
                                                            src={csp.logo_url}
                                                            alt={`${csp.name} logo`}
                                                            className="h-10 w-10 object-contain"
                                                        />
                                                    ) : (
                                                        <div className="h-10 w-10 rounded bg-content2" />
                                                    )}

                                                    <div className="flex flex-col">
                                                        <div className="font-medium">
                                                            {csp.name}
                                                        </div>
                                                        <div className="text-xs text-foreground-500">
                                                            {locationText}
                                                            {distanceText
                                                                ? ` • ${distanceText}`
                                                                : ""}
                                                        </div>
                                                    </div>
                                                </div>

                                                <Button
                                                    color="secondary"
                                                    onPress={() =>
                                                        confirmSelectCsp(csp)
                                                    }
                                                    isLoading={
                                                        setServiceProviderMutation.isPending
                                                    }
                                                    isDisabled={
                                                        setServiceProviderMutation.isPending
                                                    }
                                                >
                                                    Select
                                                </Button>
                                            </CardBody>
                                        </Card>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
